import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { useGetCryptoPayoutByIdQuery } from "../../features/cryptoPayouts/cryptoPayoutsSlice";
import {
  STATUS_LABEL,
  STATUS_CLASS,
  assetLabel,
  assetUnit,
  fmtNaira,
  fmtUsd,
} from "../../features/cryptoPayouts/status";

export default function CryptoPayoutDetails() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data: p, isLoading, isError } = useGetCryptoPayoutByIdQuery(id, { skip: !id });

  return (
    <div className="container-lg font-dashboard py-6 text-white">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-white/70 hover:text-white mb-4"
      >
        <FiArrowLeft /> Back
      </button>

      <h1 className="text-2xl font-semibold mb-6 text-white">Payout Details</h1>

      {isLoading ? (
        <p className="text-white/50">Loading…</p>
      ) : isError || !p ? (
        <p className="text-red-400">Payout not found.</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          {/* Main */}
          <div className="bg-black rounded-xl p-6 border border-primary/20 space-y-1">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white/50 text-xs">Reference</p>
                <p className="text-white font-medium break-all">{p.payoutReference}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${STATUS_CLASS[p.status]}`}>
                {STATUS_LABEL[p.status]}
              </span>
            </div>

            <Row label="Asset" value={assetLabel(p.asset)} />
            <Row label="Network" value={p.network} />
            <Row
              label="Destination"
              value={<span className="break-all text-right">{p.destinationAddress}</span>}
            />
            <Row label="You withdraw" value={fmtNaira(p.nairaAmount)} />
            <Row label="Value (USD)" value={fmtUsd(p.baseUsd)} />
            <Row label="Settlement rate" value={`₦${p.settlementRate} / $`} />
            <Row label="Coin price" value={fmtUsd(p.coinUsdPrice)} />
            <div className="border-t border-white/10 my-2" />
            <Row label="Fee" value={fmtUsd(p.fee.networkFeeUsd + p.fee.riskBufferUsd)} />
            <div className="border-t border-white/10 my-2" />
            <Row
              label={<span className="text-white">You receive</span>}
              value={
                <span className="text-white font-semibold">
                  {p.cryptoUnits} {assetUnit(p.asset)}
                  <span className="text-white/50 font-normal"> (~{fmtUsd(p.finalUsd)})</span>
                </span>
              }
            />

            {p.txHash && (
              <Row
                label="Transaction hash"
                value={<span className="break-all text-right text-primary">{p.txHash}</span>}
              />
            )}
            {p.confirmations?.target > 0 && (
              <Row label="Confirmations" value={`${p.confirmations.current} / ${p.confirmations.target}`} />
            )}
            {(p.status === "awaiting_approval" || p.status === "manual_review") && (
              <Row
                label="Note"
                value={
                  <span className="text-blue-400 text-right">
                    Your payout is being reviewed by our team. No action is needed.
                  </span>
                }
              />
            )}
            {/* Internal failure reasons (provider/admin details) are never
                shown to the user — status + refund note tell the story. */}
            {p.refundedAt && (
              <Row
                label="Refund"
                value={
                  <span className="text-green-400 text-right">
                    Amount returned to your balance
                  </span>
                }
              />
            )}
          </div>

          {/* Status — user-facing states only, not internal stages */}
          <div className="bg-black rounded-xl p-6 border border-primary/20">
            <h2 className="text-lg font-medium mb-4 text-white">Status</h2>
            <ul className="space-y-4 text-sm">
              <TimelineItem label="Requested" at={p.createdAt} done />
              {p.status === "awaiting_approval" || p.status === "manual_review" ? (
                <TimelineItem label="Under Review" done />
              ) : (
                <>
                  <TimelineItem
                    label="Processing"
                    at={p.submittedAt ?? p.broadcastAt}
                    done={
                      !!(p.submittedAt || p.broadcastAt) ||
                      ["confirmed", "failed", "reversed", "cancelled"].includes(p.status)
                    }
                  />
                  {p.status === "confirmed" ? (
                    <TimelineItem label="Completed" at={p.confirmedAt} done />
                  ) : p.status === "reversed" ? (
                    <TimelineItem label="Refunded" at={p.failedAt} done fail />
                  ) : p.status === "cancelled" ? (
                    <TimelineItem label="Cancelled" at={p.failedAt} done fail />
                  ) : p.status === "failed" ? (
                    <TimelineItem label="Failed" at={p.failedAt} done fail />
                  ) : (
                    <TimelineItem label="Completed" done={false} />
                  )}
                </>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-white/60 shrink-0">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

function TimelineItem({
  label,
  at,
  done,
  fail,
}: {
  label: string;
  at?: string;
  done?: boolean;
  fail?: boolean;
}) {
  return (
    <li className="flex items-start gap-3">
      <span
        className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${
          fail ? "bg-red-500" : done ? "bg-green-500" : "bg-white/20"
        }`}
      />
      <div>
        <p className={done ? "text-white" : "text-white/40"}>{label}</p>
        {at && <p className="text-white/40 text-xs">{new Date(at).toLocaleString()}</p>}
      </div>
    </li>
  );
}
